// Sidebar navigation: the data and the visibility rules.
//
// Split out of Sidebar.tsx so this logic can be unit-tested. Sidebar.tsx
// imports ../../lib/auth, which imports lib/msal, which constructs a
// `new PublicClientApplication(...)` at MODULE scope — so merely importing
// the component into a test spins up MSAL against a jsdom window. The
// gating rules below are pure functions over plain data and need none of
// that; keeping them here lets sidebarNav.test.ts import them directly.
//
// Nothing else changed in the move: same types, same NAV_GROUPS, same
// helpers, same behavior. See sidebarNav.test.ts for the rules they encode.

import type { IconName } from '../ui/Icon';

// ─── Types ────────────────────────────────────────────────────
export type Role = 'ceo' | 'admin' | 'manager' | 'hr' | 'recruiter' | 'coordinator' | 'viewer' | 'pending' | 'va' | null;

export interface NavItemDef {
  to: string;
  icon: IconName;
  label: string;
  /**
   * Legacy role-based gate. Kept for backward compat but prefer `permissions`.
   * null = visible to all signed-in users.
   */
  roles: Role[] | null;
  /**
   * Preferred: permission-based gate. If set, the user must hold ANY
   * of these permission keys. Falls back to `roles` when undefined.
   *
   * This gives default-deny AND honors user-specific overrides — if
   * an admin grants a recruiter access to bd.bids.view, the BD nav
   * appears for them even though their base role doesn't include it.
   */
  permissions?: string[];
}

/**
 * A collapsible nested section within a top-level group — the "nested
 * dropdown" level. Used where a group has enough items that a flat list
 * gets unwieldy (HR expanded to ~50 flat items with only a static label
 * separating them). Each subgroup owns its items directly and gates its
 * own visibility exactly like a top-level group does, so an empty subgroup
 * (every child hidden by permissions) just doesn't render — no separate
 * "orphaned header" bookkeeping needed the way the old flat
 * marker-item-followed-by-items shape required.
 */
export interface NavSubGroupDef {
  kind: 'subgroup';
  label: string;
  roles: Role[] | null;
  permissions?: string[];
  /** Starts open. Defaults to false — collapsed until the user expands it
   *  or lands on a page inside it (see SidebarSubGroup's initial state). */
  defaultOpen?: boolean;
  items: NavItemDef[];
}

export type NavEntry = NavItemDef | NavSubGroupDef;

export function isSubGroup(entry: NavEntry): entry is NavSubGroupDef {
  return (entry as NavSubGroupDef).kind === 'subgroup';
}

/**
 * Frontend-only pseudo-permission held by a demo session viewing "Full
 * access". Items that work in the demo list it next to their real key, so the
 * demo presenter sees them (the Staff Console runs as a sandbox for them —
 * backend services/demoPlatform.ts). No catalog key, so the server never
 * grants it: it changes what the menu shows, never what the API allows.
 */
export const DEMO_SESSION_KEY = 'demo.session';

/** `has`, plus DEMO_SESSION_KEY while a demo session is on "Full access"
 *  (not while previewing another role — that preview must stay faithful). */
export function withDemoSession(has: (k: string) => boolean, demoFullAccess: boolean): (k: string) => boolean {
  if (!demoFullAccess) return has;
  return (k: string) => k === DEMO_SESSION_KEY || has(k);
}

export interface NavGroupDef {
  title: string;
  roles: Role[] | null;
  /** Same semantics as NavItemDef.permissions */
  permissions?: string[];
  defaultOpen?: boolean;
  items: NavEntry[];
}

// ─── Staff Console navigation ─────────────────────────────────
// The console's own nav (StaffShell). These are the same screens that also
// stay in the workspace Settings list, at their original /settings/* paths,
// with the gates those links have always had. The console copies are an
// extra entrance for platform staff. Subscription, plan, and staff-invite
// screens are not here: they call APIs this merge does not include.
export const STAFF_NAV: NavGroupDef[] = [
  {
    title: 'Operations',
    roles: null,
    items: [
      { to: '/platform/tenant-setup', icon: 'building', label: 'Tenant setup', roles: null, permissions: ['platform.tenants.view'] },
      { to: '/platform/scheduler',    icon: 'settings', label: 'Scheduler',    roles: ['ceo', 'admin'], permissions: ['platform.tenants.view'] },
      { to: '/platform/automations',  icon: 'settings', label: 'Automations',  roles: ['ceo', 'admin'], permissions: ['platform.tenants.view'] },
      { to: '/platform/error-log',    icon: 'bug',      label: 'Error Log',    roles: ['ceo', 'admin'], permissions: ['platform.tenants.manage'] },
      // A dedicated demo build may reset its own sample data.
      { to: '/platform/demo-data',    icon: 'film',     label: 'Demo Data',    roles: ['ceo', 'admin'], permissions: ['platform.tenants.manage', DEMO_SESSION_KEY] },
    ],
  },
];

/** Workspace settings paths that also have a console entrance. The settings
 *  links stay; nothing redirects. */
export const STAFF_LEGACY_PATHS: Record<string, string> = {
  '/settings/tenants': '/platform/tenant-setup',
  '/settings/scheduler': '/platform/scheduler',
  '/settings/automations': '/platform/automations',
  '/settings/error-log': '/platform/error-log',
  '/settings/demo-data': '/platform/demo-data',
};

// ─── Navigation structure ─────────────────────────────────────
// Each nav item has EITHER legacy `roles` OR new `permissions` (or both —
// permissions wins when present). Items with `permissions` honor:
//   - role-based grants (the user has the permission through their role)
//   - user-specific overrides (admin granted this user the permission
//     directly, even though their role doesn't include it)
// Default-deny: items with `permissions` are hidden unless the user has
// at least ONE of the listed permission keys.
export const NAV_GROUPS: NavGroupDef[] = [
  {
    title: 'Overview',
    roles: null,
    defaultOpen: true,
    items: [
      { to: '/dashboard',            icon: 'dashboard',   label: 'Dashboard',         roles: null },
      // My Day consolidates Daily To-Do List (/my-work), Morning Huddle
      // (/huddle), End-of-day report (/end-of-day), Reminders (/reminders),
      // and Notifications (/notifications) into one tabbed page. Gate is the
      // most-permissive union of the five old entries (three were roles:
      // null), so nobody loses access. Old routes stay live for deep links.
      { to: '/my-day',               icon: 'sun',  label: 'My Day',            roles: null },
      // Permission-gated (rule 9): tasks.recruiter.assign is held by exactly
      // ceo/admin/manager (+ platform_admin). The old roles-only gate hid it
      // from CEOs, which every self-registered workspace owner now is.
      { to: '/management-dashboard', icon: 'chart',  label: 'Management Dashboard',  roles: ['ceo', 'manager', 'admin'], permissions: ['tasks.recruiter.assign'] },
    ],
  },
  // ─── Executive View (CEO command center) ──────────────────────
  //
  // Per Esther: she wants ONE place for everything she touches as
  // CEO — CEO Dashboard, AI Brain admin, and the daily-rhythm shortcuts
  // (Morning Huddle, Reminders) that are her morning routine. The
  // Overview group still carries Morning Huddle + Reminders for the
  // rest of the team — these duplicate-link entries are a deliberate
  // CEO convenience.
  //
  // Gate: CEO-tier permissions, so admins/managers/HR/recruiter don't
  // see this section at all.
  {
    title: 'Executive View',
    roles: ['ceo'],
    permissions: ['ceo.private_tasks', 'ceo.executive_strategy'],
    defaultOpen: true,
    items: [
      { to: '/daily-briefing', icon: 'sun', label: 'Daily Briefing', roles: null, permissions: ['ceo.private_tasks', 'ceo.executive_strategy'] },
      // One dashboard per context: CEO Dashboard lives here; the team-wide
      // Dashboard already sits under Overview. The old duplicate "Overview
      // Dashboard" entry made the exec section feel unmerged (Esther).
      { to: '/ceo-dashboard', icon: 'user-circle', label: 'CEO Dashboard',  roles: null, permissions: ['ceo.private_tasks', 'ceo.executive_strategy'] },
      { to: '/performance',   icon: 'chart', label: 'Performance & Scorecard', roles: null, permissions: ['finance.revenue_reports.view'] },
      { to: '/reports',       icon: 'trending-up', label: 'Reports',        roles: null, permissions: ['compliance.view', 'compliance.reports.export'] },
      { to: '/action-plan',   icon: 'clipboard-check', label: 'Action Plan & Tasks', roles: null, permissions: ['ai.chat.use'] },
      { to: '/client-health', icon: 'signal', label: 'Client Radar',   roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
      { to: '/ai-brain',      icon: 'sparkles', label: 'Laure — Chief of Staff', roles: null, permissions: ['admin.integrations.manage'] },
    ],
  },
  {
    title: 'Recruiting',
    roles: ['ceo', 'admin', 'manager', 'hr', 'recruiter', 'coordinator'],
    permissions: ['candidates.view', 'jobs.view', 'submissions.view', 'pipeline.view', 'tasks.recruiter.view'],
    defaultOpen: true,
    items: [
      { to: '/recruiting-dashboard', icon: 'target', label: 'Recruiting Dashboard',  roles: null, permissions: ['candidates.view'] },
      { to: '/candidates',    icon: 'candidates', label: 'Candidates',  roles: null, permissions: ['candidates.view'] },
      { to: '/pipeline/tracker', icon: 'columns', label: 'Candidate tracker', roles: null, permissions: ['candidates.view'] },

      // Collapsed by default, same as every other subgroup in this file —
      // consistent behavior across the sidebar (a dropdown starts closed
      // until you open it or land on a page inside it) beats a special case
      // for the group that happens to see the most daily traffic.
      {
        kind: 'subgroup', label: 'Sourcing', roles: null, permissions: ['candidates.view'],
        items: [
          { to: '/smart-search',  icon: 'search', label: 'Smart Search', roles: null, permissions: ['candidates.view'] },
          { to: '/bulk-lookup',   icon: 'list', label: 'Bulk Lookup', roles: null, permissions: ['candidates.view'] },
          { to: '/match-alerts',  icon: 'megaphone', label: 'Match Alerts', roles: null, permissions: ['candidates.view'] },
        ],
      },
      {
        kind: 'subgroup', label: 'Interviews', roles: null, permissions: ['candidates.view'],
        items: [
          // Queue + Scheduler are one page with two tabs (per Esther: "side by
          // side ... it doesn't even make sense to have those two there like
          // that"), so one entry. The gate is the looser of the two — the page
          // drops the HR-only Queue tab for users without hr.view rather than
          // locking them out of the Scheduler. /interviews still routes for
          // deep links and bookmarks.
          { to: '/candidates/hr-interviews', icon: 'heart', label: 'Interviews', roles: null, permissions: ['candidates.view'] },
          { to: '/recruiting/playbooks', icon: 'clipboard', label: 'Recruiting Call Scripts', roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator'], permissions: ['candidates.view'] },
        ],
      },
      {
        kind: 'subgroup', label: 'Jobs', roles: null, permissions: ['jobs.view'],
        items: [
          { to: '/jobs',          icon: 'jobs', label: 'Jobs',        roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator'], permissions: ['jobs.view'] },
          { to: '/settings/job-boards', icon: 'megaphone', label: 'Job Boards', roles: ['ceo', 'admin', 'manager'], permissions: ['jobs.edit'] },
          // Pay-rate cards live with recruiting (per Esther) — the page itself
          // stays at /finance/rate-cards and keeps its finance permission gate.
          { to: '/finance/rate-cards',  icon: 'money', label: 'Pay Rate Calculator & Stipends', roles: ['ceo', 'admin', 'manager'], permissions: ['finance.bill_rates.view'] },
        ],
      },
      {
        kind: 'subgroup', label: 'Pipeline & Follow-up', roles: null, permissions: ['submissions.view', 'candidates.view'],
        items: [
          { to: '/kanban',        icon: 'columns', label: 'Kanban Board', roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator'], permissions: ['submissions.view'] },
          { to: '/submissions',   icon: 'send', label: 'Submissions', roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator'], permissions: ['submissions.view'] },
          // Recruiter Tasks AND Conversations are both tabs of Candidate Updates /
          // Communications (per Esther: Conversations belongs "side by side" with
          // it, not as its own entry). /tasks and /conversations still route for
          // deep links and bookmarks.
          { to: '/pending-tracker', icon: 'message', label: 'Candidate Updates / Communications', roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator', 'hr'], permissions: ['candidates.view'] },
          { to: '/client-approvals', icon: 'check-circle', label: 'Client Approvals', roles: ['ceo', 'admin', 'manager', 'recruiter', 'coordinator', 'hr'], permissions: ['submissions.view', 'candidates.view'] },
          { to: '/inbound-docs',  icon: 'inbox', label: 'Inbound Docs & ShiftWise', roles: ['ceo', 'admin', 'manager', 'hr', 'recruiter', 'coordinator', 'va'], permissions: ['candidates.view'] },
        ],
      },
      {
        kind: 'subgroup', label: 'Outreach & Reports', roles: null, permissions: ['candidates.view'],
        items: [
          { to: '/sequences',     icon: 'mail', label: 'SMS / Email Campaign', roles: null, permissions: ['candidates.view'] },
          { to: '/clients-orgs',  icon: 'clients', label: 'Clients',     roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          { to: '/ats-reports',   icon: 'trending-up', label: 'Recruiting Reports', roles: ['ceo', 'admin', 'manager'], permissions: ['compliance.reports.export'] },
          { to: '/weekly-report', icon: 'calendar', label: 'Weekly Recruiting Report', roles: ['ceo', 'admin', 'manager'], permissions: ['compliance.reports.export'] },
        ],
      },
    ],
  },
  // ─── HR (consolidated) ───────────────────────────────────────
  //
  // Single sidebar home for everything an HR coordinator + their
  // managers/admins/CEO need: HR dashboard, credentialing lifecycle,
  // onboarding + eSign, compliance authoring + reporting, and staff
  // & scheduling operations. Organized into collapsible `kind: 'subgroup'`
  // sections (~50 items total) so the group is browsable a section at a
  // time instead of one long flat list.
  //
  // Permission gate: any of the HR-tier perms grants visibility.
  // Recruiters don't have any of these — so the entire HR section is
  // invisible to them. CEO/Admin/Manager inherit HR_PERMS via
  // SYSTEM_ROLES so they always see it.
  {
    title: 'HR',
    roles: ['ceo', 'admin', 'manager', 'hr', 'coordinator'],
    permissions: ['hr.view', 'credentialing.view', 'onboarding.view', 'compliance.view', 'compliance.policies.manage'],
    defaultOpen: false,
    items: [
      // HR Dashboard — top-level link to the role's home page.
      { to: '/hr-dashboard',                    icon: 'users', label: 'HR Dashboard',          roles: null, permissions: ['hr.view'] },
      { to: '/benefits',                        icon: 'gift', label: 'Benefits',               roles: null, permissions: ['hr.view'] },
      { to: '/referrals',                       icon: 'gift', label: 'Referrals & payouts',    roles: null, permissions: ['hr.view'] },

      // (Payroll Export moved to the dedicated top-level Finance section.)

      // This group alone used to unfold to ~50 flat rows the moment you
      // opened it — one long undifferentiated list. The five sections below
      // default CLOSED (unlike Recruiting's, which default open): opening
      // "HR" now shows 3 links + 5 labeled, collapsed rows to choose from,
      // and each expands independently instead of dumping everything at
      // once. Landing directly on a page inside one (a bookmark, a link from
      // elsewhere) still auto-opens that section — see SidebarSubGroup.
      {
        kind: 'subgroup', label: 'Credentialing', roles: ['ceo','admin','manager','hr','coordinator'], permissions: ['credentialing.view'],
        items: [
          { to: '/credentialing-dashboard',         icon: 'dashboard', label: 'Dashboard',              roles: null, permissions: ['credentialing.view'] },
          { to: '/credentialing/compliance-monitor', icon: 'signal', label: 'Compliance Monitor',    roles: null, permissions: ['credentialing.view'] },
          { to: '/credentialing',                   icon: 'trophy', label: 'Records',                roles: null, permissions: ['credentialing.view'] },
          // Gated on credentialing.edit, NOT .view: POST /documents/upload
          // requires credentialing_manage -> credentialing.edit, so a
          // view-only holder (recruiter) could open this page, pick a file and
          // get a 403 on Check Document. Match the route's gate so the page is
          // never visible to someone who cannot use it.
          { to: '/documents',                       icon: 'paperclip', label: 'Document Checker',       roles: null, permissions: ['credentialing.edit'] },
        ],
      },
      {
        kind: 'subgroup', label: 'Onboarding', roles: ['ceo','admin','manager','hr','coordinator'], permissions: ['onboarding.view'],
        items: [
          { to: '/new-hires',                       icon: 'sparkles', label: 'New Hires',              roles: null, permissions: ['onboarding.view'] },
          { to: '/onboarding',                      icon: 'onboarding', label: 'Active onboarding',      roles: null, permissions: ['onboarding.view'] },
          { to: '/hr/offer-letters',                icon: 'document', label: 'Offer Letters',          roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          { to: '/onboarding-packets',              icon: 'inbox', label: 'Pre-start packets',      roles: null, permissions: ['onboarding.view'] },
          { to: '/onboarding-bundle',               icon: 'edit', label: 'E-sign bundle',          roles: null, permissions: ['onboarding.view'] },
          { to: '/esign',                           icon: 'edit', label: 'eSign dashboard',        roles: null, permissions: ['onboarding.manage', 'candidates.view'] },
          { to: '/esign/documents',                 icon: 'document', label: 'eSign documents',        roles: null, permissions: ['onboarding.manage', 'candidates.view'] },
        ],
      },
      {
        // Admin authoring + org-wide reporting. The biggest subsection —
        // 17 items — and exactly the one that made "HR" unusable flat.
        kind: 'subgroup', label: 'Compliance', roles: ['ceo','admin','manager','hr','coordinator'], permissions: ['compliance.view', 'compliance.policies.manage'],
        items: [
          { to: '/compliance/admin',                icon: 'shield', label: 'Overview',               roles: null, permissions: ['compliance.view'] },
          { to: '/compliance/admin/records',        icon: 'database', label: 'All records',            roles: ['ceo','admin','manager','hr'], permissions: ['compliance.view'] },
          { to: '/compliance/admin/readiness',      icon: 'target', label: 'Placement readiness',    roles: ['ceo','admin','manager','hr'], permissions: ['compliance.view'] },
          { to: '/compliance/admin/policies',       icon: 'scale', label: 'Policies',               roles: ['ceo','admin','manager','hr'], permissions: ['compliance.policies.manage'] },
          { to: '/compliance/admin/documents',      icon: 'document', label: 'Compliance documents',   roles: ['ceo','admin','manager','hr'], permissions: ['compliance.policies.manage'] },
          { to: '/compliance/admin/exams',          icon: 'clipboard-check', label: 'Exams',                  roles: ['ceo','admin','manager','hr'], permissions: ['compliance.policies.manage'] },
          { to: '/compliance/admin/checklists',     icon: 'clipboard-check', label: 'Skills checklists',      roles: ['ceo','admin','manager','hr'], permissions: ['compliance.policies.manage'] },
          { to: '/compliance/admin/bundles',        icon: 'archive', label: 'Bundles',                roles: ['ceo','admin','manager','hr'], permissions: ['compliance.policies.manage'] },
          { to: '/compliance/admin/bulk-assign',    icon: 'pulse', label: 'Bulk assign',            roles: ['ceo','admin','manager'], permissions: ['compliance.edit'] },
          { to: '/compliance/admin/reports',        icon: 'trending-up', label: 'Compliance reports',     roles: ['ceo','admin','manager','hr'], permissions: ['compliance.reports.export'] },
          { to: '/compliance/joint-commission',     icon: 'scale', label: 'Joint Commission',       roles: ['ceo','admin','manager','hr'], permissions: ['compliance.view'] },
          { to: '/company-certifications',          icon: 'library', label: 'Company Certifications', roles: ['ceo','admin'], permissions: ['company.certifications.view'] },
          { to: '/compliance/deficiencies',         icon: 'list', label: '↳ Deficiency log',       roles: ['ceo','admin','manager','hr'], permissions: ['compliance.view'] },
          { to: '/compliance/placement-holds',      icon: 'pause', label: '↳ Placement holds',      roles: ['ceo','admin','manager','hr'], permissions: ['hr.view'] },
          { to: '/compliance/dnu-log',              icon: 'no-symbol', label: '↳ DNU log',              roles: ['ceo','admin','manager','hr'], permissions: ['hr.view'] },
          { to: '/compliance/naming-conventions',   icon: 'document-duplicate', label: '↳ Naming conventions',   roles: ['ceo','admin','manager','hr'], permissions: ['hr.view'] },
          { to: '/call-playbooks',                  icon: 'book', label: 'Playbook Builder', roles: ['ceo','admin','manager','hr','recruiter'], permissions: ['candidates.edit'] },
        ],
      },
      {
        // Was the "Workforce" top-level group.
        kind: 'subgroup', label: 'Staff & Scheduling', roles: ['ceo','admin','manager','hr','coordinator'], permissions: ['hr.view'],
        items: [
          { to: '/staff',                           icon: 'users', label: 'Staff Management',       roles: null, permissions: ['hr.view'] },
          { to: '/placements',                      icon: 'map-pin', label: 'Active Placements',      roles: ['ceo', 'admin', 'manager', 'coordinator', 'hr'], permissions: ['hr.view'] },
          { to: '/renewals',                        icon: 'refresh', label: 'Assignment Renewals',    roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          { to: '/employee-renewals',               icon: 'refresh', label: 'Renewals & Time Off',    roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          { to: '/attendance-policy',               icon: 'clock', label: 'Attendance Policy',      roles: ['ceo', 'admin', 'manager', 'hr', 'coordinator'], permissions: ['hr.view'] },
          { to: '/scheduling',                      icon: 'calendar', label: 'Scheduling',             roles: ['ceo', 'admin', 'manager', 'coordinator'], permissions: ['hr.view', 'hr.edit'] },
          // [track-E openshifts] Open-shift broadcast board — gated on scheduling.manage.
          { to: '/open-shifts',                     icon: 'megaphone', label: 'Open Shifts',            roles: ['ceo', 'admin', 'manager', 'hr', 'coordinator'], permissions: ['scheduling.manage'] },
          { to: '/attendance',                      icon: 'calendar', label: 'Attendance',             roles: ['ceo', 'admin'], permissions: ['hr.edit'] },
          { to: '/timekeeping',                     icon: 'clock', label: 'Timekeeping',            roles: ['ceo', 'admin', 'manager'], permissions: ['hr.view'] },
          { to: '/timesheet-flags',                 icon: 'flag', label: 'Timesheet Flags',        roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          // [track-B approvals] Client timesheet sign-off queue (Phase 3 Money Engine)
          { to: '/timesheet-approvals',             icon: 'check-circle', label: 'Client Timesheet Sign-off', roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['hr.view'] },
          { to: '/pto',                             icon: 'sun', label: 'PTO',                   roles: ['ceo', 'admin', 'hr', 'manager'], permissions: ['pto.view_team', 'pto.approve'] },
          { to: '/incidents',                       icon: 'alert', label: 'Incidents',             roles: ['ceo', 'admin'], permissions: ['hr.incidents.view', 'hr.incidents.manage'] },
        ],
      },
      {
        // Self-service tabs every authenticated user sees; anchored under
        // HR since HR oversees the clinician experience.
        kind: 'subgroup', label: 'Clinician Portal', roles: null,
        items: [
          { to: '/compliance/my',                   icon: 'book', label: 'My Assignments',         roles: null },
          { to: '/compliance/certificates',         icon: 'folder', label: 'My Documents',           roles: null },
          { to: '/compliance/messages',             icon: 'message', label: 'Messages',               roles: null },
        ],
      },
    ],
  },
  {
    title: 'Clients & Business Dev',
    roles: ['ceo', 'admin', 'manager', 'coordinator'],
    permissions: ['bd.leads.view', 'bd.bids.view', 'bd.contacts.view', 'candidates.view'],
    defaultOpen: false,
    items: [
      // "Facilities" is the legacy flat list. Full client management now
      // lives under Recruiting → Clients. This stays as a quick facility-
      // only shortcut (contracts, addresses).
      { to: '/clients',      icon: 'building', label: 'Facilities',            roles: ['ceo', 'admin', 'manager', 'hr', 'coordinator'], permissions: ['hr.view'] },
      { to: '/business-dev', icon: 'trending-up', label: 'Business Development',  roles: null, permissions: ['bd.leads.view', 'bd.bids.view'] },
      { to: '/gov-grants',   icon: 'trophy', label: 'Grants & Federal',      roles: null, permissions: ['bd.leads.view'] },
      { to: '/contracts',    icon: 'document', label: 'Contracts',             roles: ['ceo', 'admin', 'manager'], permissions: ['bd.contracts.view'] },
      // (Rate Cards live under Recruiting → "Pay Rate Cards & Stipends"; the
      //  duplicate entry that used to sit here was removed.)
    ],
  },
  // Finance — client invoicing + AR (track-C) and payroll export (track-D).
  // ANY-of gate so the section shows for either invoices- or payroll-authorized
  // users; each item is permission-filtered individually.
  {
    title: 'Finance',
    roles: ['ceo', 'admin'],
    permissions: ['finance.invoices.view', 'finance.payroll.view'],
    defaultOpen: false,
    items: [
      { to: '/finance/invoices', icon: 'receipt', label: 'Invoices & AR', roles: ['ceo', 'admin'], permissions: ['finance.invoices.view'] },
      { to: '/finance/payroll',  icon: 'library', label: 'Payroll Export', roles: null, permissions: ['finance.payroll.view'] },
    ],
  },
  {
    title: 'AI Advisory Board',
    roles: ['ceo', 'admin'],
    permissions: ['ai.team.use'],
    defaultOpen: false,
    items: [
      // Advisory Board is visible to anyone with ai.team.use, but WHICH advisors
      // appear on it is decided per-agent on the server — a recruiter sees
      // Regina and Harry, not Larry or Cipher.
      { to: '/ai-advisors', icon: 'users', label: 'Advisory Board',  roles: null, permissions: ['ai.team.use'] },
      // These three reach the whole board at once, or run unattended across the
      // business, so they sit behind ai.advisors.all (leadership and above)
      // rather than the general AI-team permission.
      { to: '/board-room',  icon: 'library', label: 'Board Room',      roles: null, permissions: ['ai.advisors.all'] },
      { to: '/ai-team',     icon: 'clipboard-check', label: 'Team Tasks',      roles: null, permissions: ['ai.advisors.all'] },
      // Autonomous missions run under Laure, the Chief of Staff.
      { to: '/ai-missions', icon: 'send', label: "Laure's Missions", roles: null, permissions: ['ai.advisors.all'] },
    ],
  },
  // (Removed the empty placeholder "AI Advisors" nav group — the real
  //  advisory feature ships under the "AI Advisory Board" group above.)
  {
    title: 'Tools',
    roles: ['ceo', 'admin'],
    permissions: ['ai.chat.use'],
    defaultOpen: false,
    items: [
      { to: '/ai-assistant',  icon: 'cpu', label: 'AI Chat',           roles: null, permissions: ['ai.chat.use'] },
      { to: '/ai-knowledge',  icon: 'book', label: 'Knowledge Base',    roles: null, permissions: ['ai.chat.use'] },
      { to: '/templates',     icon: 'document-duplicate', label: 'Message Templates',  roles: null, permissions: ['candidates.view', 'candidates.edit'] },
      { to: '/sms',           icon: 'chat-single', label: 'SMS Approvals',      roles: null, permissions: ['candidates.send_message'] },
      { to: '/email-monitor', icon: 'mail', label: 'Email Monitor',      roles: null, permissions: ['ai.search.email'] },
      {
        // Client-facing security services (passive / advisory only).
        kind: 'subgroup', label: 'Client Security Services', roles: ['ceo', 'admin'], permissions: ['ai.chat.use'],
        items: [
          { to: '/security/website-check',  icon: 'lock', label: 'Website Security Check',   roles: ['ceo', 'admin'], permissions: ['ai.chat.use'] },
          { to: '/security/readiness',      icon: 'shield', label: 'Readiness Assessment',    roles: ['ceo', 'admin'], permissions: ['ai.chat.use'] },
        ],
      },
    ],
  },
  {
    // Staff Console — internal platform staff only (founders and the
    // platform_owner / platform_admin / platform_operator roles). The console
    // is its own full-screen layout (StaffShell, nav = STAFF_NAV below), so
    // the app sidebar carries just the way in.
    title: 'Staff Console',
    roles: null,
    // demo.session: the demo presenter's sandboxed console (see DEMO_SESSION_KEY).
    permissions: ['platform.tenants.view', DEMO_SESSION_KEY],
    defaultOpen: false,
    items: [
      { to: '/platform',                        icon: 'building', label: 'Open Staff Console',     roles: null, permissions: ['platform.tenants.view', DEMO_SESSION_KEY] },
    ],
  },
  {
    title: 'Settings',
    roles: null,
    defaultOpen: false,
    items: [
      { to: '/settings/users',                  icon: 'users', label: 'User Management',        roles: ['ceo', 'admin', 'manager'] },
      { to: '/team-lifecycle',                  icon: 'door', label: 'Team Onboard / Offboard', roles: ['ceo', 'admin', 'manager', 'hr'], permissions: ['admin.users.manage', 'hr.edit'] },
      { to: '/security',                        icon: 'lock', label: 'Security & MFA',         roles: null },
      { to: '/settings/integrations',           icon: 'integrations', label: 'Integrations',           roles: ['ceo', 'admin'] },
      { to: '/settings/automations',            icon: 'settings', label: 'Automations',            roles: ['ceo', 'admin'] },
      { to: '/automations/approvals',           icon: 'check-circle', label: 'Email Approvals',     roles: ['ceo', 'admin'] },
      { to: '/settings/vendor-integrations',    icon: 'puzzle', label: 'Vendor Integrations',    roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      { to: '/settings/onboarding',             icon: 'settings', label: 'eBoarding Settings',     roles: ['ceo', 'admin', 'hr'], permissions: ['onboarding.manage'] },
      { to: '/settings/demo-data',              icon: 'film', label: 'Demo Data',              roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      // [phase6 track-F] Migration concierge — new-tenant import wizard, gated on tenant.import.
      { to: '/import',                          icon: 'map', label: 'Migration Concierge',   roles: ['ceo', 'admin'], permissions: ['tenant.import'] },
      { to: '/settings/tenants',                icon: 'building', label: 'Tenants',               roles: null, permissions: ['platform.tenants.view'] },
      // [phase6 track-D] White-label buyer's self-serve cockpit. Pure permission
      // gate: tenant.admin.view is owner-tier — held by ceo, tenant_admin and
      // platform_admin, never by admin.
      { to: '/tenant-admin',                    icon: 'library', label: 'Tenant Settings',       roles: null, permissions: ['tenant.admin.view'] },
      { to: '/settings/file-library',           icon: 'database', label: 'File Library',           roles: ['ceo', 'admin'], permissions: ['admin.files.manage'] },
      { to: '/settings/my-permissions',         icon: 'key', label: 'My Permissions',         roles: null },
      // Phase 8 — RBAC admin
      { to: '/settings/roles',                  icon: 'shield', label: 'Roles & Permissions',    roles: ['ceo', 'admin'], permissions: ['admin.roles.manage'] },
      { to: '/settings/user-access',            icon: 'user', label: 'User Access',            roles: ['ceo', 'admin'], permissions: ['admin.users.manage'] },
      { to: '/settings/audit-log',              icon: 'list', label: 'Security Audit Log',     roles: ['ceo', 'admin'], permissions: ['admin.security_logs.view', 'admin.ai_logs.view'] },
      // [phase6 track-H] Access Transparency — tenant admins/leadership see every
      // time platform staff (the operator) touched their data (tamper-evident).
      { to: '/settings/access-transparency',    icon: 'globe', label: 'Access Transparency',    roles: ['ceo', 'admin', 'manager'], permissions: ['tenant.transparency.view'] },
      { to: '/security/agent',                  icon: 'fingerprint', label: 'Cipher — Security Agent', roles: ['ceo', 'admin'], permissions: ['admin.security_logs.view'] },
      { to: '/settings/api-docs',               icon: 'book', label: 'API Documentation',      roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      { to: '/settings/import-tracker',         icon: 'upload', label: 'Import FNS Tracker',     roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      { to: '/settings/webhooks',               icon: 'link', label: 'Outbound Webhooks',      roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      { to: '/settings/error-log',              icon: 'bug', label: 'Error Log',              roles: ['ceo', 'admin'], permissions: ['admin.integrations.manage'] },
      { to: '/settings/scheduler',              icon: 'settings', label: 'Scheduler',              roles: ['ceo', 'admin'], permissions: ['admin.scheduler.view'] },
      { to: '/compliance/admin/categories',     icon: 'tag', label: 'Compliance Categories',  roles: ['ceo', 'admin'], permissions: ['compliance.policies.manage'] },
      { to: '/compliance/admin/notifications',  icon: 'bell', label: 'Compliance Notifications', roles: ['ceo', 'admin'], permissions: ['compliance.policies.manage'] },
      { to: '/settings/notifications',          icon: 'bell-slash', label: 'My Notification Prefs',  roles: null },
      { to: '/settings/extension-tokens',       icon: 'key', label: 'Extension Tokens',       roles: null },
      { to: '/settings/tracking-domains',       icon: 'globe', label: 'Tracking Domains',       roles: ['ceo', 'admin'], permissions: ['time_tracking_admin'] },
      { to: '/settings/time-tracking-projects', icon: 'tag', label: 'TT Projects & Tags',     roles: ['ceo', 'admin'], permissions: ['time_tracking_admin'] },
      { to: '/time-tracking',                   icon: 'clock', label: 'Work Session Tracker',   roles: null },
      { to: '/time-tracking/team',              icon: 'users', label: 'Team Time',              roles: ['ceo', 'admin', 'manager', 'hr'] },
      { to: '/help',                            icon: 'help', label: 'Help Center',            roles: null },
    ],
  },
];

// ─── Visibility helpers ───────────────────────────────────────
//
// Permission-aware filter: if the item/group declares `permissions`, the
// user must hold at least ONE of those permissions. Falls back to the
// legacy `roles` check when permissions is undefined.
//
// This gives us:
//   - Default-deny: items with `permissions: []` are hidden to users
//     who don't have any of them.
//   - Override-aware: a recruiter who's been granted bd.bids.view via
//     user override will see the BD section even though their role
//     doesn't normally include it.
//   - Backward compat: legacy role-only items still work.
// Shared by top-level groups AND subgroups — both are just a
// {roles, permissions} gate wrapped around a list of children.
export function gateIsVisible(gate: { roles: Role[] | null; permissions?: string[] }, role: Role, has: (k: string) => boolean): boolean {
  if (gate.permissions && gate.permissions.length > 0) {
    return gate.permissions.some(p => has(p));
  }
  if (gate.roles === null) return true;
  if (!role) return false;
  return (gate.roles as string[]).includes(role);
}

export function itemIsVisible(item: NavItemDef, role: Role, has: (k: string) => boolean): boolean {
  if (item.permissions && item.permissions.length > 0) {
    return item.permissions.some(p => has(p));
  }
  if (!item.roles) return true;
  if (!role) return false;
  return (item.roles as string[]).includes(role);
}

// A subgroup counts as visible only if its own gate passes AND at least one
// child survives item-level filtering — otherwise it would render as an
// empty, unopenable dropdown.
export function subGroupIsVisible(sub: NavSubGroupDef, role: Role, has: (k: string) => boolean): boolean {
  return gateIsVisible(sub, role, has) && sub.items.some((i) => itemIsVisible(i, role, has));
}

// "Does this list contain the current page?" — used only to decide the
// INITIAL (mount-time) open/closed state, so a bookmark or an incoming link
// lands with its section already expanded instead of hiding the active page
// inside a collapsed dropdown. Exact match only, matching the `end` prop on
// every NavLink below — see the comment there for why prefix matching is
// wrong here.
export function entryContainsPath(entries: NavEntry[], pathname: string): boolean {
  return entries.some((e) => (isSubGroup(e) ? e.items.some((i) => i.to === pathname) : e.to === pathname));
}
